import { useId, useState } from "react";

import { Button, Dialog, Field, Notice, Textarea, plainError } from "../../../../components/ui";

// A decision on an employee's request (approve, reject, withdraw, cancel) that may need words: a reason the employee
// will read, or — when the owner decides their own request because nobody else can — the owner's reason. It follows
// DESIGN.md › Consequential actions and confirmations: the action and subject in the title, the facts, one
// consequence line, an explicit verb, and the server's refusal kept in the dialog. The server decides who may decide.

const OWNER_OVERRIDE = "OWNER_OVERRIDE_REASON_REQUIRED";

export default function RequestDecisionDialog({
  title,
  facts,
  children,
  consequence,
  confirmLabel,
  cancelLabel = "Go back",
  tone = "default",
  reason = null, // { label, hint, required } — a note the employee sees
  failureTitle = "Not saved",
  onSubmit, // async ({ note, overrideReason })
  onClose,
}) {
  const formId = useId();
  const [note, setNote] = useState("");
  const [ownerReason, setOwnerReason] = useState("");
  const [asOwner, setAsOwner] = useState(false);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState("");
  const [invalid, setInvalid] = useState({});

  const submit = async (event) => {
    event.preventDefault();
    if (pending) return;
    const trimmed = note.trim();
    const owner = ownerReason.trim();
    const problems = {};
    if (reason?.required && !trimmed) problems.note = "Give a short reason.";
    if (asOwner && owner.length < 5) problems.owner = "Give a reason (at least 5 characters).";
    setInvalid(problems);
    if (Object.keys(problems).length) return;
    setPending(true);
    setFailure("");
    try {
      await onSubmit({ note: trimmed, overrideReason: asOwner ? owner : "" });
    } catch (error) {
      // The request is the owner's own and nobody else can review it: ask for the owner's reason and stay here.
      if (error?.serverCode === OWNER_OVERRIDE && !asOwner) setAsOwner(true);
      else setFailure(plainError(error, "The decision was not saved. Try again."));
      setPending(false);
    }
  };

  return (
    <Dialog
      open
      size="md"
      onOpenChange={(open) => { if (!open && !pending) onClose(); }}
      title={title}
      footer={
        <>
          <Button onClick={onClose} disabled={pending}>{cancelLabel}</Button>
          <Button type="submit" form={formId} variant={tone === "danger" ? "danger" : "primary"} loading={pending}>
            {asOwner ? `${confirmLabel} as owner` : confirmLabel}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={submit} noValidate className="space-y-3 text-body-sm text-secondary">
        {facts?.length ? (
          <dl className="divide-y divide-subtle border-y border-subtle">
            {facts.map((fact) => (
              <div key={fact.label} className="flex items-baseline justify-between gap-4 py-2 max-sm:flex-col max-sm:gap-0.5">
                <dt className="shrink-0 text-secondary">{fact.label}</dt>
                <dd className="min-w-0 break-words font-medium text-foreground sm:text-right">{fact.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {children}
        {consequence ? <p>{consequence}</p> : null}
        {reason ? (
          <Field label={reason.label} hint={reason.hint} error={invalid.note} required={reason.required}>
            <Textarea value={note} maxLength={500} disabled={pending} onChange={(event) => setNote(event.target.value)} />
          </Field>
        ) : null}
        {asOwner ? (
          <>
            <Notice tone="neutral" title="This is your own request">
              No one else in this business can review it, so you can decide it as the owner. Your reason is kept with the request.
            </Notice>
            <Field label="Reason for deciding your own request" error={invalid.owner} required>
              <Textarea value={ownerReason} maxLength={500} disabled={pending} onChange={(event) => setOwnerReason(event.target.value)} />
            </Field>
          </>
        ) : null}
        {failure ? <Notice tone="danger" title={failureTitle} role="alert">{failure}</Notice> : null}
      </form>
    </Dialog>
  );
}
